import { HelpOutline } from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import type React from "react";
import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Button from "~/components/ui/button";
import { CommonButton } from "~/components/ui/commonButton";
import { useNavigationPrompt } from "~/functions/navigationHooks";
import ArrowLeft from "~/images/back_arrow.svg?react";
import CheckWhite from "~/images/check_white.svg?react";
import EditWhite from "~/images/edit_white.svg?react";

const SUPPORT_STATUS_TOOLTIP = `【対応】
kintone内で完結するカスタマイズ。コード自動生成可能。

【対象外】
外部API連携（kintone.proxy()等）やREQXIGNだけでは実現できない処理。コード生成不可。`;

import { TOAST_MESSAGES } from "~/constants/messages";
import { useMarkdownRenderer } from "~/hooks/useMarkdownRenderer";
import {
  acquireLock,
  clearError,
  fetchCustomizeSpecification,
  releaseLock,
  restoreFromCopyData,
  updateCustomizeSpecification,
  updateField,
} from "~/slices/customizeSpecificationSlice";
import type { AppDispatch, RootState } from "~/store";
import styles from "./index.module.scss";

interface DetailParams extends Record<string, string | undefined> {
  selectedId: string;
  rowIndex: string;
  projectId: string;
  organizationId: string;
}

const CustomizeSpecificationDetailPage: React.FC = () => {
  const params = useParams<DetailParams>();
  const dispatch = useDispatch<AppDispatch>();
  const { currentProject, currentUser } = useSelector((state: RootState) => state.auth);
  const {
    data: detailData,
    isEditable,
    isLoading,
    error,
    fetchError,
  } = useSelector((state: RootState) => state.customizeSpecification);

  const numericRowIndex = useMemo(() => {
    const rowIndexParam = params.rowIndex;
    if (!rowIndexParam) return NaN;
    const match = rowIndexParam.match(/^rowIndex=(\d+)$/);
    return match?.[1] ? parseInt(match[1], 10) : NaN;
  }, [params.rowIndex]);

  const { renderMarkdown } = useMarkdownRenderer();

  useEffect(() => {
    if (params.selectedId && !Number.isNaN(numericRowIndex)) {
      dispatch(
        fetchCustomizeSpecification({ docId: params.selectedId, rowIndex: numericRowIndex }),
      );
    } else {
      dispatch({
        type: "customizeSpecification/fetch/rejected",
        payload: "必要なURLパラメータが無効です。",
      });
    }
  }, [dispatch, params.selectedId, numericRowIndex]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const toggleEditMode = () => {
    if (isEditable) {
      if (params.selectedId && !Number.isNaN(numericRowIndex)) {
        dispatch(
          updateCustomizeSpecification({ docId: params.selectedId, rowIndex: numericRowIndex }),
        );
      } else {
        toast.error(TOAST_MESSAGES.DOCUMENT.CUSTOMIZE_SPEC_SAVE_INFO_MISSING);
        return;
      }
    } else {
      dispatch(acquireLock());
    }
  };

  const handleUnlock = useCallback(() => {
    dispatch(releaseLock());
    dispatch(restoreFromCopyData());
  }, [dispatch]);

  useNavigationPrompt(
    isEditable,
    "編集内容が保存されていません。ページを離れてもよろしいですか？",
    async () => {
      if (params.selectedId && !Number.isNaN(numericRowIndex)) {
        await dispatch(
          updateCustomizeSpecification({ docId: params.selectedId, rowIndex: numericRowIndex }),
        );
      }
    },
    async () => {
      await handleUnlock();
    },
  );

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isEditable) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      if (isEditable) {
        handleUnlock();
      }
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isEditable, handleUnlock]);

  const goBack = () => window.history.back();

  if (isLoading) {
    return <div className={styles["pageContainer"]}>Loading...</div>;
  }

  if (fetchError) {
    return (
      <div className={styles["pageContainer"]}>
        <div className={styles["breadcrumb"]}>
          <Link
            to={`/${currentUser.organization_id}/project/${currentProject.id}/customize-specification/${params.selectedId}`}
          >
            カスタマイズ仕様書
          </Link>
          <span>&gt;</span>
          <span>エラー</span>
        </div>
        <div className={styles["headerContainer"]}>
          <div className={styles["heading"]}>
            <span className={styles["backArrow"]} onClick={goBack}></span>
            <span>エラー</span>
          </div>
        </div>
        <div className={styles["container"]}>
          <p>データの表示中にエラーが発生しました: {fetchError}</p>
          <Button onClick={goBack} label="戻る" styleType="white" />
        </div>
      </div>
    );
  }

  return (
    <div className={styles["pageContainer"]}>
      <div className={styles["breadcrumb"]}>
        <Link
          to={`/${currentUser.organization_id}/project/${currentProject.id}/customize-specification/${params.selectedId}`}
        >
          カスタマイズ仕様書
        </Link>
        <span>&gt;</span>
        <span>詳細</span>
      </div>
      <div className={styles["headerContainer"]}>
        <div className={styles["heading"]}>
          <span className={styles["backArrow"]} onClick={!isEditable ? goBack : undefined}>
            <ArrowLeft />
          </span>
          <span>{detailData?.機能名 || "詳細情報"}</span>
        </div>
        <div className={styles["buttonGroup"]}>
          {isEditable && (
            <CommonButton variant="label" onClick={handleUnlock}>
              キャンセル
            </CommonButton>
          )}
          <CommonButton icon={isEditable ? <CheckWhite /> : <EditWhite />} onClick={toggleEditMode}>
            {isEditable ? "保存" : "編集"}
          </CommonButton>
        </div>
      </div>

      <div className={styles["container"]}>
        {detailData ? (
          <>
            {/* 機能名（編集時のみ表示） */}
            {isEditable && (
              <div className={styles["section"]}>
                <h3 className={styles["subSectionHeading"]}>機能名</h3>
                <textarea
                  className={styles["textareaStyle"]}
                  value={detailData.機能名}
                  onChange={(e) =>
                    dispatch(updateField({ field: "機能名", value: e.target.value }))
                  }
                />
              </div>
            )}

            {/* 概要 */}
            {isEditable ? (
              <div className={styles["section"]}>
                <h3 className={styles["subSectionHeading"]}>概要</h3>
                <textarea
                  className={styles["textareaStyle"]}
                  value={detailData.概要}
                  onChange={(e) => dispatch(updateField({ field: "概要", value: e.target.value }))}
                />
              </div>
            ) : (
              <div className={styles["section"]}>
                <div className={`${styles["markdownContainer"]} ${styles["plainText"]}`}>
                  {renderMarkdown(detailData.概要)}
                </div>
              </div>
            )}

            {/* 機能詳細グループ */}
            <div className={styles["section"]}>
              <h2 className={styles["sectionHeading"]}>機能詳細</h2>
              {isEditable ? (
                <textarea
                  className={styles["textareaStyle"]}
                  value={detailData.機能詳細}
                  onChange={(e) =>
                    dispatch(updateField({ field: "機能詳細", value: e.target.value }))
                  }
                />
              ) : (
                <div className={`${styles["markdownContainer"]} ${styles["plainText"]}`}>
                  {renderMarkdown(detailData.機能詳細)}
                </div>
              )}
            </div>

            {/* 入力・出力情報グループ */}
            <div className={styles["section"]}>
              <h2 className={styles["sectionHeading"]}>入力・出力情報</h2>
              {[
                { key: "入力フィールド", label: "入力フィールド" },
                { key: "出力フィールド", label: "出力フィールド" },
                { key: "イベント契機", label: "イベント契機" },
                { key: "処理条件", label: "処理条件" },
              ].map(({ key, label }) => (
                <div key={key}>
                  {isEditable ? (
                    <div className={styles["section"]}>
                      <h3 className={styles["subSectionHeading"]}>{label}</h3>
                      <textarea
                        className={styles["textareaStyle"]}
                        value={detailData[key as keyof typeof detailData]}
                        onChange={(e) =>
                          dispatch(
                            updateField({
                              field: key as keyof typeof detailData,
                              value: e.target.value,
                            }),
                          )
                        }
                      />
                    </div>
                  ) : (
                    <div className={styles["tableRow"]}>
                      <div className={styles["tableHeader"]}>{label}</div>
                      <div className={styles["tableContent"]}>
                        <div className={styles["markdownContainer"]}>
                          {renderMarkdown(detailData[key as keyof typeof detailData])}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* 動作環境・設定グループ */}
            <div className={styles["section"]}>
              <h2 className={styles["sectionHeading"]}>動作環境・設定</h2>
              {[
                { key: "適用アプリ名", label: "適用アプリ名" },
                { key: "設定情報", label: "設定情報" },
                { key: "対応端末", label: "対応端末" },
              ].map(({ key, label }) => (
                <div key={key}>
                  {isEditable ? (
                    <div className={styles["section"]}>
                      <h3 className={styles["subSectionHeading"]}>{label}</h3>
                      <textarea
                        className={styles["textareaStyle"]}
                        value={detailData[key as keyof typeof detailData]}
                        onChange={(e) =>
                          dispatch(
                            updateField({
                              field: key as keyof typeof detailData,
                              value: e.target.value,
                            }),
                          )
                        }
                      />
                    </div>
                  ) : (
                    <div className={styles["tableRow"]}>
                      <div className={styles["tableHeader"]}>{label}</div>
                      <div className={styles["tableContent"]}>
                        <div className={styles["markdownContainer"]}>
                          {renderMarkdown(detailData[key as keyof typeof detailData])}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* 対応区分 */}
            {detailData.対応区分 && (
              <div className={styles["section"]}>
                <h2 className={styles["sectionHeading"]}>
                  対応区分
                  <Tooltip
                    title={<span style={{ whiteSpace: "pre-line" }}>{SUPPORT_STATUS_TOOLTIP}</span>}
                    placement="right"
                    arrow
                  >
                    <HelpOutline className={styles["helpIcon"] ?? ""} />
                  </Tooltip>
                </h2>
                {isEditable ? (
                  <select
                    className={styles["selectStyle"]}
                    value={detailData.対応区分}
                    onChange={(e) =>
                      dispatch(updateField({ field: "対応区分", value: e.target.value }))
                    }
                  >
                    <option value="対応">対応</option>
                    <option value="対象外">対象外</option>
                  </select>
                ) : (
                  <span
                    className={`${styles["supportBadge"]} ${
                      detailData.対応区分 === "対応"
                        ? styles["supportBadge--supported"]
                        : styles["supportBadge--unsupported"]
                    }`}
                  >
                    {detailData.対応区分}
                  </span>
                )}
              </div>
            )}
          </>
        ) : isLoading ? null : (
          <p>詳細データが見つかりません。</p>
        )}
      </div>
    </div>
  );
};

export default CustomizeSpecificationDetailPage;
